import { useNavigate } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useMemo } from 'react';

import type { TryOnDeps } from '@/features/tryon';
import { TryOnProvider, TryOnScreen, createTryOnApi, modeFromSearch } from '@/features/tryon';
import type { Rgb } from '@/lib/ar';
import { srgbToLinear } from '@/lib/ar';
import { API_BASE } from '@/lib/api/base';
import { closeCamera } from '@/lib/camera/barcode';
import { openStream } from '@/lib/camera/permission';
import { webShare } from '@/lib/share';

import { clientFetcher } from './bp03';
import { useClientDeps } from './deps';

/**
 * T7: the middle pixel of the frame, in linear light.
 *
 * Drawn to a 1×1 canvas, which is the browser asking the GPU to average the
 * frame down for us — cheaper and steadier than reading a block of pixels
 * and averaging them in JavaScript, and it is the whole frame rather than
 * one pixel's worth of sensor noise. The grey card is meant to fill the
 * stand's view, so the whole frame *is* the card.
 *
 * `null` when the video has not decoded anything (`readyState` 0) or when
 * the 2-D context is unavailable — a browser with canvas switched off, or
 * a `<video>` the compositor has not touched yet. `null` means "could not
 * read", which the screen reports; it never becomes a neutral sample.
 */
export function sampleFrameCentre(frame: HTMLVideoElement): Rgb | null {
  if (frame.readyState === 0) {
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const context = canvas.getContext('2d');
  if (context === null) {
    return null;
  }
  context.drawImage(frame, 0, 0, 1, 1);
  const [r = 0, g = 0, b = 0] = context.getImageData(0, 0, 1, 1).data;
  return {
    r: srgbToLinear(r / 255),
    g: srgbToLinear(g / 255),
    b: srgbToLinear(b / 255),
  };
}

/**
 * BP-21 §6: `/try-on`, and `?mode=tablet` for T7's kiosk.
 *
 * This replaces BP-11 R10's five-second placeholder outright. That screen
 * existed to prove the camera pipeline to a store reviewer and said so in
 * its own copy; leaving it beside the real one would have given the app two
 * try-on screens, one of which is a dead end.
 *
 * The three things `features/tryon` cannot own are supplied here: the
 * camera (a browser API), the clock (`performance.now`) and navigation
 * (TanStack's router, which belongs to the app). "Book this look" hands the
 * SKU to BP-04's journey as `?sku=`, which is the one handover that
 * survives a reload, a share and a back button.
 */
export function useTryOnFeatureDeps(): TryOnDeps {
  const deps = useClientDeps();
  const navigate = useNavigate();
  const api = useMemo(() => createTryOnApi(clientFetcher(deps.fetcher)), [deps.fetcher]);
  const share = webShare();
  return {
    api,
    mode: modeFromSearch(window.location.search),
    openCamera: async () => (await openStream(navigator.mediaDevices)).stream ?? null,
    closeCamera,
    // Bound rather than wrapped: an arrow here would be a function the
    // feature calls on every frame and that no test can reach except by
    // rendering a camera, which is the wrapper's only behaviour.
    now: performance.now.bind(performance),
    sampleGreyCard: sampleFrameCentre,
    // BP-22: the picture comes from the api, by the render's own id. The
    // key is not in the URL — it is a storage path, and a storage path on
    // a client's screen is one somebody will eventually try to edit.
    outputUrl: (renderId: string) => `${API_BASE}/v1/ar/renders/${renderId}/image`,
    ...(share === undefined ? {} : { shareImage: share }),
    // Nothing yet: the render loop that would apply the gains is the WebGL
    // pass, and this BP ships the seam rather than the shader. The screen
    // still shows the correction, which is what the person at the stand
    // judges it by — see `LightCheck`.
    onBookThisLook: (skuId) => {
      void navigate({ to: '/book/services', search: { sku: skuId } });
    },
  };
}

/** §6 client `/try-on`. Anonymous — §14 item 3. */
export function TryOnRoute(): JSX.Element {
  return (
    <TryOnProvider deps={useTryOnFeatureDeps()}>
      <TryOnScreen />
    </TryOnProvider>
  );
}
